// Projektteaser Freiflaechen-PV: stellt einem Projektpartner eine Flaeche vor
// (eine Flaeche je Teaser). Vorbild ist der FFPV-Flyer, aber ohne ILOS und
// ohne Eigentuemerdaten - die Regeln dazu stehen im Server und in
// src/lib/ffpvTeaser.js.
//
// Gefuehrt in vier Schritten: Deal waehlen, Angaben pruefen (mit Nachtraegen,
// die nur fuer diesen Teaser gelten), Vorschau, Erzeugen. Darunter
// eingeklappt Historie, Mapping und Vorlage (ffpvTeaserEinstellungen.jsx).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Handshake, Loader2, RefreshCw, X } from 'lucide-react';
import { T, feldStil, karteStil, knopfStil, primaerKnopfStil } from '../../lib/designTokens';
import { Ueberzeile, Wegweiser } from '../contracts/abwaegenAnzeige';
import {
  BILD_PLATZHALTER, bildAusFeld, bildVorbereiten, dealIdAusEingabe, dealKennung, erzeugenErgebnis,
  felderAufbereiten, nachtraegeBereinigen, vorschauSchluessel,
} from '../../lib/ffpvTeaser';
import { alsDateiSpeichern, teaserErzeugen, teaserLesen, teaserSenden } from './ffpvTeaserApi';
import {
  AngabenListe, BildWahl, DealWahl, ErzeugenLeiste, Fehlerkasten, TeaserVorschau,
} from './ffpvTeaserSchritte';
import TeaserEinstellungen from './ffpvTeaserEinstellungen';

const WEGWEISER = [
  ['Deal wählen', 'Aus der Warteschlange links oder per Deal-ID oben.'],
  ['Angaben prüfen', 'Was in Bitrix fehlt, nur für diesen Teaser nachtragen; bei Bedarf ein Bild hochladen.'],
  ['Erzeugen', 'PDF herunterladen oder als Kommentar in den Deal legen.'],
];

const OHNE_ERGEBNIS = { laeuft: null, ergebnis: null, fehler: '', pdf: null };

function bildFuerServer(bild) {
  return bild ? { base64: bild.base64, mimeType: bild.mimeType } : null;
}

function DealChip({ kennung, titel, onVerlassen }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 420, padding: '4px 5px 4px 11px',
      borderRadius: 999, border: `1px solid ${T.rand}`, background: T.grund,
      fontSize: T.tSm, fontWeight: 700, color: T.navy,
    }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {kennung}{titel ? ` · ${titel}` : ''}
      </span>
      <button type="button" onClick={onVerlassen} title="Anderen Deal wählen" aria-label="Anderen Deal wählen" style={{
        width: 22, height: 22, borderRadius: 999, border: 'none', background: T.flaeche, color: T.muted,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
      }}><X size={12} /></button>
    </span>
  );
}

export default function FfpvTeaserBoard({
  apiBase = '/api/contracts/usage', session = null, canCreate = false, canEdit = false,
}) {
  const accessToken = session?.access_token || null;
  const isAuthenticated = Boolean(accessToken);
  const authHeaders = useMemo(() => (accessToken ? { Authorization: `Bearer ${accessToken}` } : {}), [accessToken]);

  const [warteschlange, setWarteschlange] = useState({ items: [], stageId: '', fetchedAt: null, laedt: false, fehler: '' });
  const [historie, setHistorie] = useState({ items: [], laedt: false, fehler: '' });
  const [dealEingabe, setDealEingabe] = useState('');
  const [eingabeFehler, setEingabeFehler] = useState('');
  const [deal, setDeal] = useState(null);
  const [vorschau, setVorschau] = useState(null);
  const [vorschauStand, setVorschauStand] = useState('');
  const [vorschauLaedt, setVorschauLaedt] = useState(false);
  const [vorschauFehler, setVorschauFehler] = useState('');
  const [overrides, setOverrides] = useState({});
  const [offeneFelder, setOffeneFelder] = useState(() => new Set());
  const [bild, setBild] = useState(null);
  const [bildLaedt, setBildLaedt] = useState(false);
  const [bildFehler, setBildFehler] = useState('');
  const [phaseWechseln, setPhaseWechseln] = useState(false);
  const [erzeugen, setErzeugen] = useState(OHNE_ERGEBNIS);

  // Nur die juengste Vorschau zaehlt; aeltere Antworten (Deal gewechselt,
  // zweimal geklickt) werden verworfen, statt die neue zu ueberschreiben.
  const vorschauNr = useRef(0);
  // Der Deal, fuer den gerade gearbeitet wird - Bild und Erzeugen laufen
  // laenger und duerfen danach keinen anderen Deal beschreiben.
  const aktuellerDeal = useRef('');

  const ladeWarteschlange = useCallback(async () => {
    if (!isAuthenticated) return;
    setWarteschlange((w) => ({ ...w, laedt: true, fehler: '' }));
    try {
      const daten = await teaserLesen(apiBase, 'ffpv-teaser-pending', authHeaders);
      setWarteschlange({
        items: Array.isArray(daten?.items) ? daten.items : [],
        stageId: daten?.stageId || '',
        fetchedAt: daten?.fetchedAt || null,
        laedt: false,
        fehler: '',
      });
    } catch (fehler) {
      setWarteschlange((w) => ({ ...w, laedt: false, fehler: `Warteschlange nicht ladbar: ${fehler.message}` }));
    }
  }, [apiBase, authHeaders, isAuthenticated]);

  const ladeHistorie = useCallback(async () => {
    if (!isAuthenticated) return;
    setHistorie((h) => ({ ...h, laedt: true, fehler: '' }));
    try {
      const daten = await teaserLesen(apiBase, 'ffpv-teaser-history', authHeaders);
      setHistorie({ items: Array.isArray(daten?.items) ? daten.items : [], laedt: false, fehler: '' });
    } catch (fehler) {
      setHistorie((h) => ({ ...h, laedt: false, fehler: `Historie nicht ladbar: ${fehler.message}` }));
    }
  }, [apiBase, authHeaders, isAuthenticated]);

  useEffect(() => {
    ladeWarteschlange();
    ladeHistorie();
  }, [ladeWarteschlange, ladeHistorie]);

  const ladeVorschau = useCallback(async (stand) => {
    // Auch ein spaeter Aufruf mit altem Stand (z. B. nach dem Speichern des
    // Mappings, waehrend schon ein anderer Deal offen ist) darf die Vorschau
    // des aktuellen Deals nicht ersetzen (Code-Review 27.09.2026).
    if (!stand.dealId || stand.dealId !== aktuellerDeal.current) return;
    vorschauNr.current += 1;
    const nr = vorschauNr.current;
    setVorschauLaedt(true);
    setVorschauFehler('');
    try {
      const daten = await teaserSenden(apiBase, {
        action: 'ffpv-teaser-preview',
        dealId: stand.dealId,
        overrides: nachtraegeBereinigen(stand.overrides),
        bild: bildFuerServer(stand.bild),
      }, authHeaders);
      if (nr !== vorschauNr.current) return;
      setVorschau({
        html: String(daten?.html || ''),
        dealTitle: daten?.dealTitle || '',
        felder: Array.isArray(daten?.felder) ? daten.felder : [],
        // Zusatz des Servers (nicht im Auftrag festgelegt): das Bitrix-Bild
        // als Data-URI samt Hinweis - der Wert von FLAECHEN_BILD ist nur Text.
        bild: daten?.bild && typeof daten.bild === 'object' ? daten.bild : null,
      });
      setVorschauStand(vorschauSchluessel(stand));
    } catch (fehler) {
      if (nr === vorschauNr.current) setVorschauFehler(`Vorschau nicht möglich: ${fehler.message}`);
    } finally {
      if (nr === vorschauNr.current) setVorschauLaedt(false);
    }
  }, [apiBase, authHeaders]);

  const waehleDeal = useCallback((id, { titel = '', ausWarteschlange = false } = {}) => {
    aktuellerDeal.current = id;
    setDeal({ id, titel });
    setVorschau(null);
    setVorschauStand('');
    setOverrides({});
    setOffeneFelder(new Set());
    setBild(null);
    setBildFehler('');
    setBildLaedt(false);
    // Aus der Warteschlange heisst: Der Deal steht in "Teaser benoetigt" -
    // dann soll er nach dem Ablegen weiter. Eine eingetippte ID steht
    // womoeglich ganz woanders; die Phase dann nicht ungefragt umstellen.
    setPhaseWechseln(ausWarteschlange);
    setErzeugen(OHNE_ERGEBNIS);
    ladeVorschau({ dealId: id, overrides: {}, bild: null });
  }, [ladeVorschau]);

  const dealVerlassen = () => {
    aktuellerDeal.current = '';
    vorschauNr.current += 1;
    setDeal(null);
    setVorschau(null);
    setVorschauLaedt(false);
    setVorschauFehler('');
    setBildLaedt(false);
    setDealEingabe('');
    setErzeugen(OHNE_ERGEBNIS);
  };

  const dealOeffnen = (ereignis) => {
    ereignis.preventDefault();
    const id = dealIdAusEingabe(dealEingabe);
    if (!id) {
      setEingabeFehler('Bitte eine Deal-ID eingeben – etwa D_1234, 1234 oder den Link aus Bitrix.');
      return;
    }
    setEingabeFehler('');
    setDealEingabe('');
    const inWarteschlange = warteschlange.items.find((item) => String(item.dealId) === id);
    waehleDeal(id, { titel: inWarteschlange?.title || '', ausWarteschlange: Boolean(inWarteschlange) });
  };

  const stand = { dealId: deal?.id || '', overrides, bild };
  const veraltet = Boolean(vorschau) && vorschauSchluessel(stand) !== vorschauStand;
  const aktualisieren = () => ladeVorschau(stand);

  const oeffneFeld = (platzhalter) => setOffeneFelder((offen) => new Set(offen).add(platzhalter));
  const setzeNachtrag = (platzhalter, wert) => setOverrides((alt) => ({ ...alt, [platzhalter]: wert }));
  const verwirfNachtrag = (platzhalter) => {
    setOverrides(({ [platzhalter]: _verworfen, ...rest }) => rest);
    setOffeneFelder((offen) => new Set([...offen].filter((p) => p !== platzhalter)));
  };

  const bildGewaehlt = async (datei) => {
    const dealId = deal?.id;
    if (!dealId) return;
    setBildLaedt(true);
    setBildFehler('');
    try {
      const neu = await bildVorbereiten(datei);
      if (aktuellerDeal.current !== dealId) return;
      setBild(neu);
      // Ein Bild ist eine bewusste Handlung - die Vorschau gleich nachziehen.
      ladeVorschau({ dealId, overrides, bild: neu });
    } catch (fehler) {
      if (aktuellerDeal.current === dealId) setBildFehler(fehler.message);
    } finally {
      // Nur fuer den eigenen Deal - sonst gaebe ein altes Bild die Sperre
      // eines neuen frei, das noch verkleinert wird.
      if (aktuellerDeal.current === dealId) setBildLaedt(false);
    }
  };

  const bildVerwerfen = () => {
    setBild(null);
    setBildFehler('');
    ladeVorschau({ ...stand, bild: null });
  };

  const erzeuge = async (anBitrix) => {
    if (!deal || !canCreate) return;
    const dealId = deal.id;
    const wechseln = anBitrix && phaseWechseln;
    setErzeugen({ ...OHNE_ERGEBNIS, laeuft: anBitrix ? 'bitrix' : 'pdf' });
    try {
      const antwort = await teaserErzeugen(apiBase, {
        dealId,
        overrides: nachtraegeBereinigen(overrides),
        bild: bildFuerServer(bild),
        anBitrix,
        phaseWechseln: wechseln,
      }, authHeaders);
      const pdf = { blob: antwort.blob, dateiname: antwort.dateiname };
      if (!anBitrix) alsDateiSpeichern(pdf.blob, pdf.dateiname);
      if (aktuellerDeal.current === dealId) {
        setErzeugen({
          ...OHNE_ERGEBNIS,
          ergebnis: anBitrix
            ? erzeugenErgebnis({ bitrix: antwort.bitrix, phase: antwort.phase, anBitrix, phaseWechseln: wechseln })
            : { ton: 'gut', zeilen: [`PDF heruntergeladen: ${pdf.dateiname}`] },
          // Beim Ablegen im Deal wird nicht ungefragt heruntergeladen; die
          // Datei bleibt aber zum Speichern da.
          pdf: anBitrix ? pdf : null,
        });
      }
    } catch (fehler) {
      if (aktuellerDeal.current === dealId) setErzeugen({ ...OHNE_ERGEBNIS, fehler: `Nicht erzeugt: ${fehler.message}` });
    }
    ladeHistorie();
    // Mit dem Phasenwechsel verlaesst der Deal die Warteschlange.
    if (wechseln) ladeWarteschlange();
  };

  const aufbereitet = useMemo(() => felderAufbereiten(vorschau?.felder, overrides), [vorschau, overrides]);
  const bildFeld = (vorschau?.felder || []).find((feld) => feld?.platzhalter === BILD_PLATZHALTER);
  // Bitrix-Bild fuer das Vorschaubild: bild.src der Vorschau, sonst ein <img>
  // im Feldwert. Ein hochgeladenes zeigt BildWahl aus dem eigenen Stand.
  const bitrixBildQuelle = bildAusFeld(vorschau?.bild?.src)
    || bildAusFeld(bildFeld?.quelle === 'nachtrag' ? '' : bildFeld?.wert);

  let sperrGrund = '';
  if (bildLaedt) sperrGrund = 'Das Bild wird noch vorbereitet …';
  else if (vorschauLaedt) sperrGrund = 'Die Vorschau wird erstellt …';
  else if (!vorschau) sperrGrund = 'Erst muss die Vorschau stehen.';
  else if (veraltet) sperrGrund = 'Erst die Vorschau aktualisieren – sie zeigt noch den alten Stand.';

  const neuLaden = () => {
    ladeWarteschlange();
    ladeHistorie();
    if (deal) aktualisieren();
  };

  const nachEinstellung = (meldung) => {
    toast.success(meldung);
    if (deal) ladeVorschau(stand);
  };

  const dealTitel = vorschau?.dealTitle || deal?.titel || '';
  const laedtEtwas = warteschlange.laedt || historie.laedt || vorschauLaedt;

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', background: T.grund }}>
      <div style={{ padding: '16px 20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Kopf: Titel, gewaehlter Deal, Deal-ID, Neu laden. */}
        <div style={{ ...karteStil, padding: '11px 14px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 4 }}>
            <Handshake size={17} color={T.navy} />
            <span style={{ fontSize: T.tPanel, fontWeight: 700, color: T.navy, letterSpacing: T.spurTitel }}>Projektteaser FFPV</span>
          </div>
          {deal ? <DealChip kennung={dealKennung(deal.id)} titel={dealTitel} onVerlassen={dealVerlassen} /> : null}
          <span style={{ flex: 1 }} />
          <form onSubmit={dealOeffnen} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              value={dealEingabe}
              onChange={(e) => { setDealEingabe(e.target.value); setEingabeFehler(''); }}
              placeholder="Deal-ID, z. B. D_1234"
              aria-label="Deal-ID"
              aria-invalid={Boolean(eingabeFehler)}
              disabled={!isAuthenticated}
              style={{ ...feldStil, width: 190, padding: '7px 10px' }}
            />
            <button type="submit" disabled={!isAuthenticated || !dealEingabe.trim()}
              style={{ ...(deal ? knopfStil() : primaerKnopfStil), opacity: !isAuthenticated || !dealEingabe.trim() ? 0.45 : 1 }}>
              Öffnen
            </button>
          </form>
          {warteschlange.fetchedAt ? (
            <span style={{ fontSize: T.tMikro, color: T.muted }}>
              Stand {new Date(warteschlange.fetchedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
            </span>
          ) : null}
          <button type="button" onClick={neuLaden} disabled={!isAuthenticated || laedtEtwas} title="Neu laden" aria-label="Neu laden"
            style={{ ...knopfStil(), padding: '6px 8px', opacity: !isAuthenticated || laedtEtwas ? 0.55 : 1 }}>
            {laedtEtwas ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          </button>
        </div>

        {!isAuthenticated ? <Fehlerkasten>Bitte einloggen – ohne Anmeldung liefert der Server keine Deals.</Fehlerkasten> : null}
        <Fehlerkasten>{eingabeFehler}</Fehlerkasten>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-start' }}>
          <section style={{ ...karteStil, flex: '1 1 380px', minWidth: 0, padding: 16 }}>
            {deal ? (
              <AngabenListe
                aufbereitet={aufbereitet}
                offen={offeneFelder}
                gesperrt={Boolean(erzeugen.laeuft)}
                veraltet={veraltet}
                laedt={vorschauLaedt}
                fehler={vorschau ? '' : vorschauFehler}
                // Ohne Vorschau (Deal nicht ladbar) gibt es auch kein Bild zu zeigen.
                bildBaustein={vorschau ? (
                  <BildWahl
                    quelle={bitrixBildQuelle}
                    herkunftQuelle={vorschau.bild?.quelle || bildFeld?.quelle}
                    hinweis={bild ? '' : String(vorschau.bild?.hinweis || '')}
                    bild={bild}
                    laedt={bildLaedt}
                    fehler={bildFehler}
                    gesperrt={Boolean(erzeugen.laeuft)}
                    onDatei={bildGewaehlt}
                    onEntfernen={bildVerwerfen}
                  />
                ) : null}
                onOeffnen={oeffneFeld}
                onNachtrag={setzeNachtrag}
                onZuruecksetzen={verwirfNachtrag}
                onAktualisieren={aktualisieren}
              />
            ) : (
              <DealWahl
                items={warteschlange.items}
                laedt={warteschlange.laedt}
                fehler={warteschlange.fehler}
                onWahl={(item) => waehleDeal(String(item.dealId), { titel: item.title || '', ausWarteschlange: true })}
              />
            )}
          </section>

          <section style={{ ...karteStil, flex: '1.35 1 480px', minWidth: 0, padding: 16 }}>
            {deal ? (
              <TeaserVorschau
                html={vorschau?.html || ''}
                laedt={vorschauLaedt}
                veraltet={veraltet}
                fehler={vorschau ? vorschauFehler : ''}
                onAktualisieren={aktualisieren}
              />
            ) : (
              <>
                <Ueberzeile>So entsteht ein Teaser</Ueberzeile>
                <Wegweiser schritte={WEGWEISER} />
                <div style={{ maxWidth: 420, margin: '22px auto 8px', fontSize: T.tMikro, color: T.muted, lineHeight: 1.55 }}>
                  Der Teaser geht an einen Projektpartner: Standort mit Anschrift der Fläche, Gemarkung,
                  Flur und Flurstück, keine Eigentümerdaten.
                </div>
              </>
            )}
          </section>
        </div>

        {deal ? (
          <ErzeugenLeiste
            darfErzeugen={canCreate}
            bereit={!sperrGrund}
            sperrGrund={sperrGrund}
            fehlendPflicht={aufbereitet.fehlendPflicht}
            veraltet={veraltet && !vorschauLaedt && !bildLaedt}
            onAktualisieren={aktualisieren}
            laeuft={erzeugen.laeuft}
            phaseWechseln={phaseWechseln}
            onPhaseWechseln={setPhaseWechseln}
            onHerunterladen={() => erzeuge(false)}
            onInDenDeal={() => erzeuge(true)}
            ergebnis={erzeugen.ergebnis}
            fehler={erzeugen.fehler}
            pdf={erzeugen.pdf}
            onPdfSpeichern={() => erzeugen.pdf && alsDateiSpeichern(erzeugen.pdf.blob, erzeugen.pdf.dateiname)}
          />
        ) : null}

        <TeaserEinstellungen
          apiBase={apiBase}
          authHeaders={authHeaders}
          isAuthenticated={isAuthenticated}
          canCreate={canCreate}
          canEdit={canEdit}
          historie={historie}
          onGeaendert={nachEinstellung}
        />
      </div>
    </div>
  );
}
